import React, { useState, useMemo } from "react";
import {
  HELP_ARTICLES,
  CATEGORY_ORDER,
  CATEGORY_ICONS,
  getArticlesByCategory,
  findArticleById,
} from "../data/helpArticles";

// ─── Color constants — match the existing navy palette in TopBar/App ──
// Pulled directly from TopBar.jsx hex values so the page feels native.
const C = {
  bg:        "#040b14",   // app background (matches App.js T.appBg)
  panel:     "#060d18",   // sidebar panel (matches TopBar)
  panelAlt:  "#0a1628",   // raised surface (modal-style)
  border:    "#162840",   // hairline border
  borderAlt: "#1e3a54",   // slightly stronger border
  text:      "#e8f4ff",   // primary text
  textMid:   "#c8dff0",   // secondary text
  textDim:   "#8aa8c5",   // body copy
  textMute:  "#6a9fc0",   // labels
  textHint:  "#3a6a8a",   // small hints
  accent:    "#3399ff",   // brand blue
  accentDim: "#3399ff44",
  green:     "#00cc88",   // success
  orange:    "#ff9900",   // warning
};

// ─── Block renderer — renders one content block from an article ────────
// Articles are arrays of typed blocks (paragraph, h2, list, info, warn, kbd).
// Avoids needing react-markdown for v1 — pure JS data + JSX.
function Block({ block }) {
  if (block.type === "p") {
    return (
      <p style={{ color: C.textDim, fontSize: 13, lineHeight: 1.7, margin: "0 0 14px" }}>
        {block.text}
      </p>
    );
  }
  if (block.type === "h2") {
    return (
      <h2 style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: "20px 0 8px" }}>
        {block.text}
      </h2>
    );
  }
  if (block.type === "list") {
    return (
      <ul style={{ color: C.textDim, fontSize: 13, lineHeight: 1.7, margin: "0 0 14px", paddingLeft: 20 }}>
        {block.items.map((item, i) => (
          <li key={i} style={{ marginBottom: 4 }}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "info") {
    // Blue info callout — for tips and supplementary context
    return (
      <div style={{
        background: "rgba(51,153,255,0.08)",
        border: `1px solid ${C.accentDim}`,
        borderRadius: 6,
        padding: "12px 16px",
        margin: "0 0 14px",
      }}>
        <div style={{ color: C.accent, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          {block.title}
        </div>
        <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.6 }}>{block.text}</div>
      </div>
    );
  }
  if (block.type === "warn") {
    // Orange warning callout — for things users might trip over
    return (
      <div style={{
        background: "rgba(255,153,0,0.08)",
        border: "1px solid rgba(255,153,0,0.3)",
        borderRadius: 6,
        padding: "12px 16px",
        margin: "0 0 14px",
      }}>
        <div style={{ color: C.orange, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          {block.title}
        </div>
        <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.6 }}>{block.text}</div>
      </div>
    );
  }
  if (block.type === "kbd") {
    // Keyboard shortcut row
    return (
      <div style={{ display: "flex", gap: 6, margin: "0 0 14px", flexWrap: "wrap" }}>
        {block.keys.map((key, i) => (
          <span key={i} style={{
            background: C.panel,
            border: `1px solid ${C.borderAlt}`,
            borderRadius: 4,
            padding: "3px 10px",
            fontSize: 11,
            color: C.textMid,
            fontFamily: "monospace",
          }}>
            {key}
          </span>
        ))}
      </div>
    );
  }
  if (block.type === "kbdRow") {
    // Two-column keyboard shortcut row: styled key on left, description on right.
    // Used in the "Keyboard shortcuts (full reference)" article.
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "6px 0",
        borderBottom: `1px solid ${C.border}`,
      }}>
        <span style={{
          background: C.panel,
          border: `1px solid ${C.borderAlt}`,
          borderRadius: 4,
          padding: "3px 10px",
          fontSize: 11,
          color: C.textMid,
          fontFamily: "monospace",
          minWidth: 130,
          textAlign: "center",
          flexShrink: 0,
        }}>
          {block.key}
        </span>
        <span style={{ color: C.textDim, fontSize: 13 }}>{block.desc}</span>
      </div>
    );
  }
  return null;
}

export default function HelpPage() {
  // Selected article — defaults to "welcome" so the right pane is never empty
  const [selectedId, setSelectedId] = useState("welcome");
  // Search query — filters article titles in the sidebar
  const [search, setSearch] = useState("");

  // Compute article groupings once. Search returns a flat list when active.
  const grouped = useMemo(() => getArticlesByCategory(), []);
  const filtered = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return HELP_ARTICLES.filter((a) => a.title.toLowerCase().includes(q));
  }, [search]);

  const article = findArticleById(selectedId);

  return (
    <div style={{
      flex: 1,
      display: "flex",
      overflow: "hidden",
      background: C.bg,
      fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
    }}>

      {/* ═══ LEFT — article list sidebar ═══════════════════════ */}
      <div style={{
        width: 260,
        flexShrink: 0,
        background: C.panel,
        borderRight: `1px solid ${C.border}`,
        overflow: "auto",
        padding: "16px 0",
      }}>
        {/* Search input */}
        <div style={{ padding: "0 16px 12px" }}>
          <input
            type="text"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              background: C.bg,
              border: `1px solid ${C.borderAlt}`,
              borderRadius: 6,
              padding: "8px 10px",
              color: C.text,
              fontSize: 12,
              fontFamily: "inherit",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Article list — flat when searching, grouped otherwise */}
        {filtered ? (
          <div>
            {filtered.length === 0 ? (
              <div style={{ padding: "12px 16px", color: C.textHint, fontSize: 12 }}>
                No articles found
              </div>
            ) : (
              filtered.map((a) => (
                <ArticleLink
                  key={a.id}
                  article={a}
                  active={a.id === selectedId}
                  onClick={() => setSelectedId(a.id)}
                />
              ))
            )}
          </div>
        ) : (
          CATEGORY_ORDER.map((cat) => (
            <div key={cat} style={{ marginBottom: 8 }}>
              <div style={{
                padding: "8px 16px 4px",
                color: C.textHint,
                fontSize: 9,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}>
                {CATEGORY_ICONS[cat]} {cat}
              </div>
              {grouped[cat].map((a) => (
                <ArticleLink
                  key={a.id}
                  article={a}
                  active={a.id === selectedId}
                  onClick={() => setSelectedId(a.id)}
                />
              ))}
            </div>
          ))
        )}
      </div>

      {/* ═══ RIGHT — article content ══════════════════════════ */}
      <div style={{ flex: 1, overflow: "auto", padding: "32px 48px" }}>
        {article ? (
          <div style={{ maxWidth: 720 }}>
            {/* Breadcrumb */}
            <div style={{ color: C.textHint, fontSize: 11, marginBottom: 8 }}>
              {article.category}
              <span style={{ margin: "0 6px" }}>›</span>
              {article.title}
            </div>

            {/* Title + meta */}
            <h1 style={{ color: C.text, fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>
              {article.title}
            </h1>
            <div style={{ color: C.textHint, fontSize: 11, marginBottom: 24 }}>
              Last updated: {article.lastUpdated} · {article.readMins} min read
            </div>

            {/* Content blocks */}
            {article.sections.map((block, i) => (
              <Block key={i} block={block} />
            ))}

            {/* Feedback row — visual only for now */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 0 0",
              marginTop: 24,
              borderTop: `1px solid ${C.border}`,
            }}>
              <span style={{ color: C.textHint, fontSize: 11 }}>Was this helpful?</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={feedbackBtn} title="Yes, helpful">👍</button>
                <button style={feedbackBtn} title="Not helpful">👎</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: C.textHint }}>Select an article to read.</div>
        )}
      </div>
    </div>
  );
}

// ─── Sidebar article link — extracted for readability ────────
function ArticleLink({ article, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "8px 16px",
        color: active ? C.text : C.textMute,
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        background: active ? "rgba(51,153,255,0.08)" : "transparent",
        borderLeft: `3px solid ${active ? C.accent : "transparent"}`,
        cursor: "pointer",
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "rgba(51,153,255,0.04)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      {article.title}
    </div>
  );
}

// Reusable style for thumbs-up/down buttons
const feedbackBtn = {
  background: "transparent",
  border: "1px solid #1e3a54",
  borderRadius: 6,
  padding: "4px 12px",
  fontSize: 13,
  cursor: "pointer",
  fontFamily: "inherit",
};
